// utils/fetchX.js
import { generateCode } from 'utils/helpers';
import appService from 'services/appService';
import { interceptApiCall } from 'services/apiTracker';
import { getParams, getQuery } from 'utils/urlUtils';
import { onProduction } from 'utils/helpers';
import dayjs from 'dayjs';

/**
 * Fetch wrapper with logging and API tracking (similar to axios interceptors)
 * Orange-colored logs for external API requests (vs green for internal backend)
 */
const fetchX = async (url, options = {}) => {
  const requestCode = generateCode([2, 3], 6);
  const timestamp = +new Date();
  const method = (options.method || 'GET').toUpperCase();
  const data = options.body ? JSON.parse(options.body) : {};
  const headers = options.headers || {};

  // API Tracker: Intercept before request (throws if aborted)
  interceptApiCall(url, method.toLowerCase(), data, headers);

  // REQUEST LOG GROUP (DARK ORANGE) - DEVELOPMENT ONLY
  if (!onProduction()) {
    const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
    const groupTitle = `${dayjs().format('HH:mm:ss.SSS')} 🌐 ${requestCode} ${method} ${urlWithoutProtocol} | ➡️ EXTERNAL REQUEST`;
    console.groupCollapsed('%c' + groupTitle, 'color: #ff8800; font-weight: bold;'); // Dark orange
    console.log('📍 URL:', url);
    console.log('🔗 Params:', getParams(url, url));
    console.log('🔍 Query:', getQuery(url));
    console.log('🔧 Method:', method);
    console.log('📦 Headers:', headers);

    if (options.body) {
      console.log('📦 Body (Object):', data);

      try {
        console.log('%c📋 Body (JSON - Copy to Postman):', 'color: #ff9800; font-weight: bold;');
        console.log(JSON.stringify(data, null, 2));
        console.log('%c📋 Body (One-line):', 'color: #ff9800;');
        console.log(JSON.stringify(data));
      } catch (e) {
        console.warn('⚠️ Não foi possível stringificar o body:', e);
      }
    }

    // Stack trace para rastrear origem da chamada
    console.groupCollapsed('%c🎯 Stack', 'font-weight: normal');
    console.trace();
    console.groupEnd();

    console.groupEnd();
  }

  try {
    const response = await fetch(url, options);
    const totalTime = +new Date() - timestamp;

    // RESPONSE LOG GROUP (LIGHT ORANGE) - DEVELOPMENT ONLY
    if (!onProduction()) {
      let responseData = null;
      let responseText = null;

      try {
        // Clone response to read body without consuming it
        const clonedResponse = response.clone();
        responseText = await clonedResponse.text();

        // Try to parse as JSON
        if (responseText) {
          try {
            responseData = JSON.parse(responseText);
          } catch {
            responseData = responseText; // Keep as text if not JSON
          }
        }
      } catch (e) {
        console.warn('⚠️ Não foi possível ler o response body:', e);
      }

      const noSuccess = !response.ok;
      const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
      const responseTitle = noSuccess
        ? `${dayjs().format('HH:mm:ss.SSS')} ⚠️ ${requestCode} ${method} ${urlWithoutProtocol} ${totalTime}ms | ⬅️ EXTERNAL RESPONSE`
        : `${dayjs().format('HH:mm:ss.SSS')} ✅ ${requestCode} ${method} ${urlWithoutProtocol} ${totalTime}ms | ⬅️ EXTERNAL RESPONSE`;

      const responseColor = noSuccess
        ? 'color: #ffaa44; font-weight: bold;'
        : 'color: #ffcc66; font-weight: bold;'; // warning orange : light orange

      console.groupCollapsed('%c' + responseTitle, responseColor);
      console.log('📍 URL:', url);
      console.log('📊 Status:', response.status, response.statusText);
      console.log('📦 Response Headers:', Object.fromEntries(response.headers.entries()));

      if (responseData !== null) {
        console.log('📦 Response Data (Object):', responseData);

        try {
          console.log('%c📋 Response (JSON - Copy):', 'color: #ffa726; font-weight: bold;');
          console.log(
            typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2)
          );
          if (typeof responseData !== 'string') {
            console.log('%c📋 Response (One-line):', 'color: #ffa726;');
            console.log(JSON.stringify(responseData));
          }
        } catch (e) {
          console.warn('⚠️ Não foi possível stringificar a resposta:', e);
        }
      }

      // Stack trace para rastrear origem da chamada
      console.groupCollapsed('%c🎯 Stack', 'font-weight: normal');
      console.trace();
      console.groupEnd();

      console.groupEnd();
    }

    return response;
  } catch (error) {
    const totalTime = +new Date() - timestamp;

    // ERROR LOG GROUP (RED) - DEVELOPMENT ONLY
    if (!onProduction()) {
      const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
      const errorResponseTitle = `${dayjs().format('HH:mm:ss.SSS')} ❌ ${requestCode} ${method} ${urlWithoutProtocol} ${totalTime}ms | EXTERNAL ERROR`;
      console.groupCollapsed('%c' + errorResponseTitle, 'color: #ff6b6b; font-weight: bold;'); // Red
      console.error('📍 URL:', url);
      console.error('📊 Status:', error?.status || 'Network Error');
      console.error('🔗 Params:', getParams(url, url));
      console.error('🔍 Query:', getQuery(url));
      console.error('📦 Error Message:', error?.message || 'Unknown error');
      console.error('📤 Request Payload:', options.body ? data : null);
      console.error('⏱️ Total Time:', `${totalTime}ms`);

      if (options.body) {
        try {
          console.log('%c📋 Request Payload (JSON - Copy):', 'color: #ff6b6b; font-weight: bold;');
          console.error(JSON.stringify(data, null, 2));
        } catch (e) {
          console.warn('⚠️ Não foi possível stringificar o payload:', e);
        }
      }

      // Stack trace para rastrear origem do erro
      console.groupCollapsed('%c🎯 Stack', 'font-weight: normal');
      console.trace();
      console.groupEnd();
      console.groupEnd();
    }

    // Re-throw error
    throw error;
  }
};

export default fetchX;
